import { MenuItem } from "@mui/material";
import { Form } from "formik";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import { getArts } from "../../../../actions/arts";
import FormikAutocomplete from "../../../commons/formik/formikAutocomplete";
import FormikDatePicker from "../../../commons/formik/formikDatePicker";
import FormikForm from "../../../commons/formik/formikForm";
import FormikSelect from "../../../commons/formik/formikSelect";
import FormikTextField from "../../../commons/formik/formikTextField";
import { StyledNovedadesSearchForm } from "../../../styled/connectivity";

const validationSchema = yup.object().shape({
    art: yup.string().required("ART es obligatoria"),
    tipoFormulario: yup.string().required("Tipo de Formulario es obligatorio"),
    dni: yup
        .string()
        .matches(/^[0-9]*$/, "Ingrese sólo números")
        .test('dni-or-prestador-required', 'Debe completar DNI o Prestador', function (value) {
            const { prestador } = this.parent;
            return !!(value && value.trim()) || !!prestador;
        }),
    prestador: yup
        .mixed()
        .nullable()
        .test('dni-or-prestador-required', 'Debe completar DNI o Prestador', function (value) {
            const { dni } = this.parent;
            return !!(value) || !!(dni && dni.trim());
        }),
    fechaDesde: yup.date().required("Fecha Desde es obligatoria"),
    fechaHasta: yup.date()
        .required("Fecha Hasta es obligatoria")
        .test('is-after-fechaDesde', 'La fecha hasta debe ser mayor o igual a la fecha desde', function (value) {
            const { fechaDesde } = this.parent;
            if (fechaDesde && value) {
                return new Date(value) >= new Date(fechaDesde);
            }
            return true;
        })
        .test('max-7-days-diff', 'La diferencia entre fechas no puede ser mayor a 7 días', function (value) {
            const { fechaDesde } = this.parent;
            if (fechaDesde && value) {
                const diff = Math.abs(new Date(value) - new Date(fechaDesde));
                const maxDiff = 7 * 24 * 60 * 60 * 1000; // 7 días en milisegundos
                return diff <= maxDiff;
            }
            return true;
        }),
});

const initialValues = {
    art: '',
    tipoFormulario: '',
    dni: '',
    prestador: '',
    fechaDesde: null,
    fechaHasta: null,
};

const tipoFormularioOptions = [
    { value: "Alta", label: "AM" },
    { value: "Ingreso", label: "IAMI" },
    { value: "Evolucion", label: "IET" },
    { value: "Autorizacion", label: "FPA" },
    { value: "Traslado", label: "FPT" },
    { value: "Turno", label: "Turno" },
];

const XmlNewsSearch = ({ isSearching, handleSearch, handleClear, onArtChange }) => {
    const dispatch = useDispatch();
    const arts = useSelector((state) => state.arts.list);
    const providers = useSelector((state) => state.providers.allProvidersOptions);
    const loading = useSelector((state) => state.arts.isFetching);

    const roles = useSelector((state) => state.auth.roles);

    useEffect(() => {
        if ((roles.SOPORTE || roles.ADMIN) && arts.length === 0 && !loading) {
            dispatch(getArts());
        }
    }, [dispatch, arts.length, loading, roles.SOPORTE, roles.ADMIN]);

    const handleSubmitSearch = (values) => {
        handleSearch(values);
    };

    const handleClearForm = (resetForm) => {
        resetForm();
        if (onArtChange) {
            onArtChange(null);
        }
        if (handleClear) {
            handleClear();
        }
    };

    return (
        <div>
            <FormikForm
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmitSearch}>
                {({ resetForm, setFieldValue, values }) => (
                    <Form>
                        <StyledNovedadesSearchForm.StyledPaper>
                            <StyledNovedadesSearchForm.StyledInput>
                                <FormikSelect
                                    name="art"
                                    label="Art"
                                    variant="standard"
                                    value={values.art}
                                    callBack={(newValue) => {
                                        setFieldValue('prestador', '');
                                        const selectedArt = arts.find(art => art.id === newValue);
                                        if (onArtChange) {
                                            onArtChange(selectedArt);
                                        }
                                    }}>
                                    {arts.map((art) => {
                                        return (
                                            <MenuItem value={art.id} key={art.id}>
                                                {art.nombre}
                                            </MenuItem>
                                        );
                                    })}
                                </FormikSelect>
                            </StyledNovedadesSearchForm.StyledInput>
                            <StyledNovedadesSearchForm.StyledInput>
                                <FormikSelect
                                    name="tipoFormulario"
                                    label="Tipo de Formulario"
                                    variant="standard">
                                    {tipoFormularioOptions.map((option) => (
                                        <MenuItem value={option.value} key={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </FormikSelect>
                            </StyledNovedadesSearchForm.StyledInput>
                            <StyledNovedadesSearchForm.StyledInput>
                                <FormikTextField
                                    name="dni"
                                    label="DNI"
                                    variant="standard"
                                    inputProps={{
                                        onBlur: (e) => {
                                            setFieldValue('dni', e.target.value.trim());
                                        },
                                        onKeyDown: (e) => {
                                            if (e.key === ' ') {
                                                e.preventDefault();
                                            }
                                        }
                                    }}
                                />
                            </StyledNovedadesSearchForm.StyledInput>
                            <StyledNovedadesSearchForm.StyledInput>
                                <FormikAutocomplete
                                    name="prestador"
                                    label="Prestador"
                                    variant="standard"
                                    options={providers.data || []}
                                    getOptionLabel={(option) => `${option.cuit || ''} ${option.nombre || option.razonSocial || ''}`}
                                    renderOption={(option) => `${option.cuit}, ${option.nombre.trim()}`}
                                    filterOptions={(option) => `${option.cuit || ''} ${option.nombre || ''}`}
                                    getOptionValue={(option) => option?.cuit || option}
                                    isOptionEqualToValue={(option, value) => option?.cuit === value?.cuit}
                                    disabled={!values.art || !providers.data || providers.data.length === 0}
                                    noOptionsText="Seleccione primero una ART"
                                    returnProp="cuit"
                                />
                            </StyledNovedadesSearchForm.StyledInput>
                            <StyledNovedadesSearchForm.StyledInput>
                                <FormikDatePicker
                                    name="fechaDesde"
                                    label="Fecha Desde"
                                    variant="standard"
                                />
                            </StyledNovedadesSearchForm.StyledInput>
                            <StyledNovedadesSearchForm.StyledInput>
                                <FormikDatePicker
                                    name="fechaHasta"
                                    label="Fecha Hasta"
                                    variant="standard"
                                />
                            </StyledNovedadesSearchForm.StyledInput>

                            <StyledNovedadesSearchForm.ButtonContainer>
                                <StyledNovedadesSearchForm.StyledButton
                                    variant="contained"
                                    type="button"
                                    onClick={() => handleClearForm(resetForm)}
                                    disabled={isSearching}>
                                    Limpiar
                                </StyledNovedadesSearchForm.StyledButton>
                                <StyledNovedadesSearchForm.StyledButton
                                    variant="contained"
                                    type="submit"
                                    color="primary"
                                    disabled={isSearching}>
                                    Buscar
                                </StyledNovedadesSearchForm.StyledButton>
                            </StyledNovedadesSearchForm.ButtonContainer>
                        </StyledNovedadesSearchForm.StyledPaper>
                    </Form>
                )}
            </FormikForm>
        </div >
    );
};

export default XmlNewsSearch;